import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, Sparkles } from "lucide-react";
import { LOGO_URL } from "../lib/site";
import { useVisibilityMount } from "../hooks/useVisibilityMount";

const HINTS = [
  {
    eyebrow: "Fastest route",
    text: "Need a quick answer? Jump straight to WhatsApp and share your date.",
  },
  {
    eyebrow: "Best first detail",
    text: "City plus event date helps us reply with real availability much faster.",
  },
  {
    eyebrow: "Luxury fit",
    text: "Choose the celebration format first and we shape the lineup around it.",
  },
];

const createGlowTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const gradient = ctx.createRadialGradient(128, 128, 10, 128, 128, 128);
  gradient.addColorStop(0, "rgba(255,232,168,0.9)");
  gradient.addColorStop(0.35, "rgba(212,175,55,0.26)");
  gradient.addColorStop(1, "rgba(212,175,55,0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 256, 256);

  return new THREE.CanvasTexture(canvas);
};

const getLiteAssistant = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const memory = navigator.deviceMemory ?? 8;
  const cores = navigator.hardwareConcurrency ?? 8;
  const reducedMotion =
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

  return reducedMotion || memory <= 6 || cores <= 6 || window.innerWidth < 820;
};

export const BookingAssistant = ({ whatsappHref, callHref }) => {
  const mountRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const [assistantRef, shouldRenderAssistant] = useVisibilityMount({
    rootMargin: "260px 0px",
    once: false,
  });
  const [liteAssistant, setLiteAssistant] = useState(getLiteAssistant);
  const [hintIndex, setHintIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const handleResize = () => setLiteAssistant(getLiteAssistant());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setHintIndex((current) => (current + 1) % HINTS.length);
    }, 3600);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!shouldRenderAssistant || liteAssistant) {
      return undefined;
    }

    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      34,
      mount.clientWidth / mount.clientHeight,
      0.1,
      100
    );
    camera.position.set(0, 0, 5.2);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    const getTargetPixelRatio = () =>
      Math.min(window.devicePixelRatio, window.innerWidth < 768 ? 1 : 1.4);
    renderer.setPixelRatio(getTargetPixelRatio());
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xfff0cc, 1.05);
    scene.add(ambient);

    const key = new THREE.PointLight(0xffd58f, 16, 18, 1.8);
    key.position.set(2.1, 2.3, 3.4);
    scene.add(key);

    const rim = new THREE.PointLight(0xffa640, 7, 14, 2);
    rim.position.set(-2.4, -1.3, 2.6);
    scene.add(rim);

    const group = new THREE.Group();
    scene.add(group);

    const medallion = new THREE.Group();
    group.add(medallion);

    const edgeMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xb78534,
      emissive: 0x432008,
      emissiveIntensity: 0.22,
      roughness: 0.34,
      metalness: 0.9,
      clearcoat: 0.6,
      clearcoatRoughness: 0.24,
    });

    const faceMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x2f1c10,
      emissive: 0x241306,
      emissiveIntensity: 0.36,
      roughness: 0.42,
      metalness: 0.52,
      clearcoat: 0.72,
      clearcoatRoughness: 0.18,
    });

    const insetMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x170d08,
      emissive: 0x211106,
      emissiveIntensity: 0.34,
      roughness: 0.48,
      metalness: 0.3,
      clearcoat: 0.54,
      clearcoatRoughness: 0.22,
    });

    const etchMaterial = new THREE.MeshStandardMaterial({
      color: 0xe2bb70,
      emissive: 0x5a300a,
      emissiveIntensity: 0.11,
      roughness: 0.26,
      metalness: 0.84,
    });

    const markerMaterial = new THREE.MeshStandardMaterial({
      color: 0xcf9f4f,
      emissive: 0x4f2808,
      emissiveIntensity: 0.12,
      roughness: 0.34,
      metalness: 0.82,
    });

    const dotMaterial = new THREE.MeshStandardMaterial({
      color: 0xffe0a2,
      emissive: 0x8a4f10,
      emissiveIntensity: 0.16,
      roughness: 0.32,
      metalness: 0.72,
    });

    const coinBody = new THREE.Mesh(
      new THREE.CylinderGeometry(0.98, 0.98, 0.3, 72, 1, false),
      edgeMaterial
    );
    coinBody.rotation.x = Math.PI / 2;
    medallion.add(coinBody);

    const backPlate = new THREE.Mesh(
      new THREE.CylinderGeometry(0.92, 0.96, 0.05, 72),
      faceMaterial
    );
    backPlate.rotation.x = Math.PI / 2;
    backPlate.position.z = -0.125;
    medallion.add(backPlate);

    const frontPlate = new THREE.Mesh(
      new THREE.CylinderGeometry(0.9, 0.94, 0.06, 72),
      faceMaterial
    );
    frontPlate.rotation.x = Math.PI / 2;
    frontPlate.position.z = 0.115;
    medallion.add(frontPlate);

    const innerDish = new THREE.Mesh(
      new THREE.CylinderGeometry(0.74, 0.78, 0.035, 64),
      insetMaterial
    );
    innerDish.rotation.x = Math.PI / 2;
    innerDish.position.z = 0.138;
    medallion.add(innerDish);

    const innerFace = new THREE.Mesh(
      new THREE.CylinderGeometry(0.63, 0.67, 0.024, 64),
      new THREE.MeshPhysicalMaterial({
        color: 0x120a06,
        emissive: 0x1c0f05,
        emissiveIntensity: 0.26,
        roughness: 0.52,
        metalness: 0.18,
        clearcoat: 0.3,
        clearcoatRoughness: 0.28,
      })
    );
    innerFace.rotation.x = Math.PI / 2;
    innerFace.position.z = 0.148;
    medallion.add(innerFace);

    const bezel = new THREE.Mesh(
      new THREE.TorusGeometry(0.86, 0.075, 22, 108),
      edgeMaterial
    );
    bezel.position.z = 0.152;
    medallion.add(bezel);

    const innerBezel = new THREE.Mesh(
      new THREE.TorusGeometry(0.69, 0.018, 16, 88),
      new THREE.MeshStandardMaterial({
        color: 0xffe6a9,
        emissive: 0x6a3608,
        emissiveIntensity: 0.18,
        roughness: 0.16,
        metalness: 0.92,
      })
    );
    innerBezel.position.z = 0.152;
    medallion.add(innerBezel);

    const engravedRingOuter = new THREE.Mesh(
      new THREE.TorusGeometry(0.58, 0.012, 12, 84),
      etchMaterial
    );
    engravedRingOuter.position.z = 0.16;
    medallion.add(engravedRingOuter);

    const engravedRingInner = new THREE.Mesh(
      new THREE.TorusGeometry(0.44, 0.008, 10, 72),
      etchMaterial
    );
    engravedRingInner.position.z = 0.162;
    medallion.add(engravedRingInner);

    const crestHalo = new THREE.Mesh(
      new THREE.CircleGeometry(1.06, 64),
      new THREE.MeshBasicMaterial({
        color: 0xffd885,
        transparent: true,
        opacity: 0.06,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    crestHalo.position.z = -0.17;
    medallion.add(crestHalo);

    const logoMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 1,
      depthWrite: false,
      alphaTest: 0.04,
    });
    const logoShadowMaterial = new THREE.MeshBasicMaterial({
      color: 0x5a2b08,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
      alphaTest: 0.04,
    });

    const logoShadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1.18, 1.54),
      logoShadowMaterial
    );
    logoShadow.position.set(0.032, -0.04, 0.164);
    medallion.add(logoShadow);

    const logoPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(1.12, 1.46),
      logoMaterial
    );
    logoPlane.position.z = 0.195;
    medallion.add(logoPlane);

    const logoHighlight = new THREE.Mesh(
      new THREE.PlaneGeometry(1.1, 1.44),
      new THREE.MeshBasicMaterial({
        color: 0xffefc3,
        transparent: true,
        opacity: 0.16,
        depthWrite: false,
        alphaTest: 0.04,
        blending: THREE.AdditiveBlending,
      })
    );
    logoHighlight.position.set(-0.016, 0.018, 0.202);
    medallion.add(logoHighlight);

    const ringA = new THREE.Mesh(
      new THREE.TorusGeometry(1.22, 0.034, 18, 96),
      new THREE.MeshStandardMaterial({
        color: 0xd5a24e,
        emissive: 0x4c2508,
        emissiveIntensity: 0.18,
        roughness: 0.34,
        metalness: 0.88,
      })
    );
    ringA.rotation.x = Math.PI / 2.95;
    group.add(ringA);

    const ringB = new THREE.Mesh(
      new THREE.TorusGeometry(0.94, 0.02, 14, 84),
      new THREE.MeshStandardMaterial({
        color: 0xf0d29a,
        emissive: 0x3f2208,
        emissiveIntensity: 0.14,
        roughness: 0.26,
        metalness: 0.82,
      })
    );
    ringB.rotation.y = Math.PI / 2.55;
    ringB.rotation.x = 0.62;
    group.add(ringB);

    const ringC = new THREE.Mesh(
      new THREE.TorusGeometry(1.06, 0.014, 14, 80),
      new THREE.MeshStandardMaterial({
        color: 0xe3c07c,
        emissive: 0x4f2809,
        emissiveIntensity: 0.08,
        roughness: 0.24,
        metalness: 0.84,
      })
    );
    ringC.rotation.y = Math.PI / 3.6;
    ringC.rotation.x = -0.34;
    group.add(ringC);

    const finMaterial = new THREE.MeshStandardMaterial({
      color: 0xc68d29,
      emissive: 0x522607,
      emissiveIntensity: 0.25,
      roughness: 0.4,
      metalness: 0.76,
    });

    [-1, 1].forEach((side) => {
      const fin = new THREE.Mesh(
        new THREE.ConeGeometry(0.14, 0.44, 4),
        finMaterial
      );
      fin.rotation.z = side * Math.PI * 0.5;
      fin.rotation.x = Math.PI * 0.25;
      fin.position.set(side * 0.96, 0.08, -0.04);
      group.add(fin);
    });

    const satellitePivotA = new THREE.Group();
    const satelliteA = new THREE.Mesh(
      new THREE.SphereGeometry(0.13, 20, 20),
      new THREE.MeshStandardMaterial({
        color: 0xfff2cf,
        emissive: 0xd4af37,
        emissiveIntensity: 0.45,
        roughness: 0.2,
        metalness: 0.84,
      })
    );
    satelliteA.position.set(1.6, 0, 0);
    satellitePivotA.add(satelliteA);
    group.add(satellitePivotA);

    const satellitePivotB = new THREE.Group();
    const satelliteB = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 16, 16),
      new THREE.MeshStandardMaterial({
        color: 0xffd996,
        emissive: 0xffb34c,
        emissiveIntensity: 0.35,
        roughness: 0.28,
        metalness: 0.72,
      })
    );
    satelliteB.position.set(-1.25, 0.55, 0.14);
    satellitePivotB.rotation.z = 1;
    satellitePivotB.add(satelliteB);
    group.add(satellitePivotB);

    const detailDots = [];
    for (let i = 0; i < 6; i += 1) {
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.034, 14, 14),
        dotMaterial
      );
      const angle = (i / 6) * Math.PI * 2;
      dot.position.set(Math.cos(angle) * 0.88, Math.sin(angle) * 0.88, 0.18);
      medallion.add(dot);
      detailDots.push(dot);
    }

    const engravings = [];
    for (let i = 0; i < 12; i += 1) {
      const marker = new THREE.Mesh(
        new THREE.BoxGeometry(0.022, 0.11, 0.012),
        markerMaterial
      );
      const angle = (i / 12) * Math.PI * 2;
      marker.position.set(Math.cos(angle) * 0.735, Math.sin(angle) * 0.735, 0.162);
      marker.rotation.z = angle;
      medallion.add(marker);
      engravings.push(marker);
    }

    const glowTexture = createGlowTexture();
    const glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTexture,
        transparent: true,
        opacity: 0.55,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    glow.scale.set(3.4, 3.4, 1);
    scene.add(glow);

    const textureLoader = new THREE.TextureLoader();
    const logoTexture = textureLoader.load(LOGO_URL, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
      logoMaterial.map = texture;
      logoShadowMaterial.map = texture;
      logoHighlight.material.map = texture;
      logoMaterial.needsUpdate = true;
      logoShadowMaterial.needsUpdate = true;
      logoHighlight.material.needsUpdate = true;
    });

    const onPointerMove = (event) => {
      const rect = mount.getBoundingClientRect();
      const nx = (event.clientX - rect.left) / rect.width - 0.5;
      const ny = (event.clientY - rect.top) / rect.height - 0.5;
      pointerRef.current.tx = nx * 0.6;
      pointerRef.current.ty = ny * 0.45;
    };

    const onPointerLeave = () => {
      pointerRef.current.tx = 0;
      pointerRef.current.ty = 0;
    };

    mount.addEventListener("pointermove", onPointerMove);
    mount.addEventListener("pointerleave", onPointerLeave);

    let frameId = 0;
    const clock = new THREE.Clock();

    const animate = () => {
      const t = clock.getElapsedTime();
      const pointer = pointerRef.current;
      pointer.x += (pointer.tx - pointer.x) * 0.06;
      pointer.y += (pointer.ty - pointer.y) * 0.06;

      group.position.y = Math.sin(t * 1.25) * 0.12;
      group.rotation.y = t * 0.32 + pointer.x * 0.5;
      group.rotation.x = Math.sin(t * 0.55) * 0.08 - pointer.y * 0.55;
      group.rotation.z = Math.sin(t * 0.8) * 0.04;

      logoPlane.rotation.z = Math.sin(t * 0.68) * 0.035;
      logoShadow.rotation.z = logoPlane.rotation.z;
      logoHighlight.rotation.z = logoPlane.rotation.z;
      medallion.rotation.z = Math.sin(t * 0.4) * 0.02;
      medallion.rotation.x = Math.sin(t * 0.34) * 0.06;
      crestHalo.material.opacity = 0.045 + Math.sin(t * 1.2) * 0.015;
      crestHalo.scale.setScalar(1 + Math.sin(t * 0.9) * 0.015);
      ringA.rotation.z += 0.0048;
      ringB.rotation.z -= 0.0058;
      ringC.rotation.z += 0.0038;
      satellitePivotA.rotation.y += 0.018;
      satellitePivotA.rotation.x = Math.sin(t * 1.2) * 0.28;
      satellitePivotB.rotation.y -= 0.014;
      detailDots.forEach((dot, index) => {
        dot.position.z = 0.18 + Math.sin(t * 1.1 + index * 0.8) * 0.018;
      });
      engravings.forEach((marker, index) => {
        marker.scale.y = 1 + Math.sin(t * 0.7 + index * 0.5) * 0.04;
      });

      glow.material.opacity = 0.3 + Math.sin(t * 1.4) * 0.05;
      glow.scale.setScalar(3.05 + Math.sin(t * 1.1) * 0.08);

      renderer.render(scene, camera);
      frameId = window.requestAnimationFrame(animate);
    };

    animate();

    const onResize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      renderer.setPixelRatio(getTargetPixelRatio());
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      mount.removeEventListener("pointermove", onPointerMove);
      mount.removeEventListener("pointerleave", onPointerLeave);

      glowTexture?.dispose?.();
      logoTexture?.dispose?.();
      coinBody.geometry.dispose();
      coinBody.material.dispose();
      backPlate.geometry.dispose();
      backPlate.material.dispose();
      frontPlate.geometry.dispose();
      frontPlate.material.dispose();
      innerDish.geometry.dispose();
      innerDish.material.dispose();
      innerFace.geometry.dispose();
      innerFace.material.dispose();
      bezel.geometry.dispose();
      bezel.material.dispose();
      innerBezel.geometry.dispose();
      innerBezel.material.dispose();
      engravedRingOuter.geometry.dispose();
      engravedRingOuter.material.dispose();
      engravedRingInner.geometry.dispose();
      engravedRingInner.material.dispose();
      crestHalo.geometry.dispose();
      crestHalo.material.dispose();
      logoPlane.geometry.dispose();
      logoPlane.material.dispose();
      logoShadow.geometry.dispose();
      logoShadow.material.dispose();
      logoHighlight.geometry.dispose();
      logoHighlight.material.dispose();
      ringA.geometry.dispose();
      ringA.material.dispose();
      ringB.geometry.dispose();
      ringB.material.dispose();
      ringC.geometry.dispose();
      ringC.material.dispose();
      satelliteA.geometry.dispose();
      satelliteA.material.dispose();
      satelliteB.geometry.dispose();
      satelliteB.material.dispose();
      detailDots.forEach((dot) => {
        dot.geometry.dispose();
        dot.material.dispose();
      });
      engravings.forEach((marker) => {
        marker.geometry.dispose();
        marker.material.dispose();
      });
      finMaterial.dispose();
      edgeMaterial.dispose();
      faceMaterial.dispose();
      insetMaterial.dispose();
      etchMaterial.dispose();
      markerMaterial.dispose();
      dotMaterial.dispose();
      glow.material.dispose();
      renderer.dispose();

      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [liteAssistant, shouldRenderAssistant]);

  const currentHint = HINTS[hintIndex];

  return (
    <motion.div
      ref={assistantRef}
      className="relative mx-auto mb-6 w-full max-w-[250px] md:absolute md:right-[-26px] md:top-8 md:z-30 md:mb-0 lg:right-[-42px]"
      animate={{
        y: [0, -8, 0, 6, 0],
        x: [0, 5, 0, -4, 0],
        rotate: [0, 1.2, 0, -1.2, 0],
      }}
      transition={{
        duration: 9,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <div className="pointer-events-auto relative">
        <div className="absolute inset-x-10 top-5 h-24 rounded-full bg-[#d4af37]/18 blur-3xl" />

        <button
          type="button"
          onClick={() => {
            setExpanded((current) => !current);
            setHintIndex((current) => (current + 1) % HINTS.length);
          }}
          onMouseEnter={() => setExpanded(true)}
          onFocus={() => setExpanded(true)}
          className="group relative ml-auto flex h-[118px] w-[118px] items-center justify-center rounded-full border border-[#d4af37]/25 bg-[radial-gradient(circle_at_30%_30%,rgba(255,214,132,0.14),rgba(12,10,8,0.92)_68%)] shadow-[0_26px_80px_-30px_rgba(212,175,55,0.55)] backdrop-blur"
          data-testid="booking-assistant-toggle"
          aria-label="Open booking concierge assistant"
        >
          <div ref={mountRef} className="absolute inset-0" />
          {!shouldRenderAssistant || liteAssistant ? (
            <img
              src={LOGO_URL}
              alt=""
              aria-hidden="true"
              className="pointer-events-none relative z-[1] h-[60px] w-auto object-contain opacity-95 drop-shadow-[0_0_18px_rgba(212,175,55,0.35)]"
              draggable={false}
            />
          ) : null}
          <div className="absolute left-2 top-3 inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/30 px-2 py-1 text-[9px] uppercase tracking-[0.26em] text-[#d4af37]">
            <Sparkles size={10} />
            Live
          </div>
          <div className="pointer-events-none absolute inset-0 rounded-full border border-white/8 group-hover:border-[#d4af37]/35" />
        </button>

        <AnimatePresence mode="wait">
          <motion.div
            key={hintIndex}
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.35 }}
            className="mt-3 rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(18,14,10,0.98)_0%,rgba(8,8,8,0.96)_100%)] p-4 shadow-2xl shadow-black/55 backdrop-blur-xl"
          >
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#d4af37]">
              {expanded ? "AB Concierge" : currentHint.eyebrow}
            </div>
            <p className="mt-2 text-sm font-light leading-relaxed text-white/66">
              {currentHint.text}
            </p>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.28 }}
                  className="overflow-hidden"
                >
                  <div className="flex gap-2">
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="booking-assistant-whatsapp"
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] px-3 py-2 text-xs font-medium text-white transition-transform duration-300 hover:translate-y-[-1px]"
                    >
                      <MessageCircle size={14} />
                      WhatsApp
                    </a>
                    <a
                      href={callHref}
                      data-testid="booking-assistant-call"
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-[#d4af37]/35 px-3 py-2 text-xs font-medium text-white/80 transition-colors duration-300 hover:border-[#d4af37] hover:text-white"
                    >
                      <Phone size={14} />
                      Call
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default BookingAssistant;
